// AddAccommodation.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../shared/components/ui/Button';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm, Controller } from 'react-hook-form';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import { uploadFilesToS3 } from '../../services/api/implementation/impl';

export const AddAccommodation = ({
  onSubmitIndependent,
  onSubmitGroup,
  onSubmitParking,
  independentData,
  groupData,
  parkingData,
  isSubmitting,
  ShowValue,
  onClose
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    control
  } = useForm({
    defaultValues: {
      north_length: '',
      south_length: '',
      east_length: '',
      west_length: '',
      plotType: 'regular',
      measurementUnit: Cookies.get('measurement_type') || 'sq/ft',
      loadingFactor: ''
    }
  });

  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;

  const [isShow, setisShow] = useState('');
  const [plotArea, setPlotArea] = useState('');
  const [cadFileLink, setCadFileLink] = useState([]);
  const [isUploadingCadFile, setIsUploadingCadFile] = useState(false);

  // Watch relevant fields
  const watchedPlotType = watch('plotType', 'regular');
  const watchedWidth = watch('width', '');
  const watchedDepth = watch('length', '');
  const userId = Cookies.get('userId');
  //write a useeffect to reset form on on watchedPlotType change

  // Track page view
  useEffect(() => {
    mixpanel.track('Add Accommodation Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    setValue('width', '');
    setValue('length', '');
    setValue('east_length', '');
    setValue('west_length', '');
    setValue('north_length', '');
    setValue('south_length', '');
    setValue('plotArea', '');
  }, [watchedPlotType, setValue]);

  // irregular plot fields
  const watchedEast = watch('east_length', '');
  const watchedWest = watch('west_length', '');
  const watchedNorth = watch('north_length', '');
  const watchedSouth = watch('south_length', '');
  const watchedGroupBuildUpArea = watch('buildUpArea', '');
  const watchedGroupLoadingFactor = watch('loadingFactor', '');

  const calculatedSuperBuildUpArea = useMemo(() => {
    const area = parseFloat(watchedGroupBuildUpArea) || 0;
    const factor = parseFloat(watchedGroupLoadingFactor) || 0;
    if (!area) return '';
    if (!factor) return String(area);
    return String(Math.round(area * (1 + factor / 100) * 100) / 100);
  }, [watchedGroupBuildUpArea, watchedGroupLoadingFactor]);

  useEffect(() => {
    if (calculatedSuperBuildUpArea !== '') {
      setValue('superBuildUpArea', calculatedSuperBuildUpArea);
    }
  }, [calculatedSuperBuildUpArea, setValue]);

  // Handle Accommodation Type Change
  const handleOnChangeType = (e) => {
    setisShow(e.target.value);
    setValue('accomodationType', e.target.value);
    setCadFileLink([]);
  };

  // Handle CAD file upload to S3 (append to cadFileLink array like uploadedFiles)
  const handleCadFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setIsUploadingCadFile(true);
    try {
      const uploadedFiles = await uploadFilesToS3([file]);
      if (uploadedFiles?.length > 0) {
        setCadFileLink((prev) => [
          ...prev,
          { name: uploadedFiles[0].name, url: uploadedFiles[0].url }
        ]);
        toast.success('CAD file uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading CAD file:', error);
      toast.error('Failed to upload CAD file');
    } finally {
      setIsUploadingCadFile(false);
      event.target.value = '';
    }
  };

  const removeCadFile = (index) => {
    setCadFileLink((prev) => prev.filter((_, i) => i !== index));
  };

  // Calculate Plot Area for regular Plots
  useEffect(() => {
    if (isShow === 'INDEPENDENT' && watchedPlotType === 'regular') {
      const width = parseFloat(watchedWidth);
      const length = parseFloat(watchedDepth);
      if (!isNaN(width) && !isNaN(length)) {
        const calculatedPlotArea = (width * length).toFixed(2);
        setPlotArea(calculatedPlotArea);
        setValue('plotArea', calculatedPlotArea);
      } else {
        setPlotArea('');
        setValue('plotArea', '');
      }
    }
    // Reset plot area for irregular plots
    if (isShow === 'INDEPENDENT' && watchedPlotType === 'irregular') {
      setPlotArea('');
      setValue('plotArea', '');
    }
  }, [watchedWidth, watchedDepth, watchedPlotType, isShow, setValue]);

  const handleCarpetAreaChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^0-9.]/g, '').slice(0, 8);
      setValue('carpetArea', cleanedValue);
    },
    [setValue]
  );

  const handleBuildUpAreaChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^0-9.]/g, '').slice(0, 8);
      setValue('buildUpArea', cleanedValue);
    },
    [setValue]
  );

  const handleLoadingFactorChange = useCallback(
    (val) => {
      let inputVal = val.replace(/\D/g, '');
      const numericVal = parseInt(inputVal, 10);
      if (!isNaN(numericVal) && numericVal > 100) {
        inputVal = '100';
        toast.error('Loading factor cannot exceed 100');
      }
      setValue('loadingFactor', inputVal);
    },
    [setValue]
  );

  const handleSuperBuildUpAreaChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^0-9.]/g, '').slice(0, 8);
      setValue('superBuildUpArea', cleanedValue);
    },
    [setValue]
  );

  const handlePlotAreaChange = useCallback(
    (val) => {
      const cleanedValue = val.replace(/[^0-9.]/g, '').slice(0, 8);
      setValue('plotArea', cleanedValue);
      setPlotArea(cleanedValue);
    },
    [setValue]
  );

  // Add numeric input handlers
  const handleNumericInput = useCallback(
    (field, val) => {
      const cleanedValue = val.replace(/[^0-9.]/g, '').slice(0, 8);
      setValue(field, cleanedValue);
    },
    [setValue]
  );

  const MultipleFiles = useSelector((state) => state.file.multifiles);

  console.log('This is the File State');
  console.log(MultipleFiles);

  const onSubmitHandler = (data) => {
    if (isShow === 'INDEPENDENT' && parseFloat(data.buildUpArea) > 0 && !data.tag) {
      toast.error('Please select an Accommodation.');
      return;
    }
    if (isShow === 'GROUP' && parseFloat(data.buildUpArea) > 0 && !data.tag) {
      toast.error('Please select an Accommodation.');
      return;
    }
    if (isShow === 'INDEPENDENT') {
      const transformedData = {
        ...data,
        plotArea: watchedPlotType === 'regular' ? plotArea : data.plotArea,
        plotType: watchedPlotType,
        ...(watchedPlotType === 'regular'
          ? { width: data.width, length: data.length }
          : {
              east_length: data.east_length,
              west_length: data.west_length,
              north_length: data.north_length,
              south_length: data.south_length
            }),
        numberOfFloors: data.numberOfFloors ? parseInt(data.numberOfFloors) : undefined,
        floorPlanLink: MultipleFiles?.floorPlanLink || [],
        unitSpecification: MultipleFiles?.unitSpecification || [],
        materialListLink: MultipleFiles?.materialListLink || [],
        tag: data.tag,
        ...(cadFileLink?.length > 0 && { cadFileLink })
      };
      onSubmitIndependent(transformedData);
    } else if (isShow === 'GROUP') {
      const transformedData = {
        ...data,
        floorPlanLink: MultipleFiles?.floorPlanLink || [],
        unitSpecification: MultipleFiles?.unitSpecification || [],
        materialListLink: MultipleFiles?.materialListLink || [],
        ...(cadFileLink?.length > 0 && { cadFileLink })
      };
      onSubmitGroup(transformedData);
    } else if (isShow === 'PARKING') {
      const parkingData = {
        ...data,
        ...(cadFileLink?.length > 0 && { cadFileLink })
      };
      onSubmitParking(parkingData);
    }
  };

  return (
    <div>
      <div>
        <div className="flex flex-col justify-between items-start gap-4">
          <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
            Accommodation Details
          </span>
        </div>
        <form onSubmit={handleSubmit(onSubmitHandler)} className="flex flex-col gap-6 pt-6">
          <div className="flex gap-10 justify-between items-center w-full">
            <div className="text-md flex flex-col gap-2 w-[48%]">
              <span className="text-gray-600">
                Accommodation Type <span className="text-red-500"> *</span>
              </span>
              <select
                onChange={handleOnChangeType}
                className="w-full p-2 border-[1px] border-stone-300 rounded"
                defaultValue="">
                <option value="" disabled>
                  Select Accommodation Type
                </option>
                <option value="INDEPENDENT">Independent</option>
                <option value="GROUP">Group</option>
                <option value="PARKING">Parking</option>
              </select>
            </div>

            <FormInput
              width={48}
              label={'Accommodation Name'}
              register={register}
              register_key={'name'}
              isRequired={true}
              type={'text'}
              validation={{ required: 'Accommodation name is required' }}
              errors={errors}
            />

            <input type="hidden" name="accomodationType" {...register('accomodationType')} />
          </div>

          {/* Render form based on Accommodation Type */}
          {isShow === 'INDEPENDENT' && (
            <div className="flex flex-col gap-6">
              {/* Unit Type and Plot Type Fields */}
              <div className="flex justify-between items-center w-full">
                <FormInput
                  width={48}
                  label={'Unit Type'}
                  register={register}
                  register_key={'unitType'}
                  isRequired={true}
                  type={'dropdown'}
                  options={[
                    { value: '', label: 'Select Unit Type', disabled: true },
                    { value: 'Residential', label: 'Residential' },
                    { value: 'Commercial', label: 'Commercial' }
                  ]}
                  validation={{ required: 'Unit Type is required' }}
                  errors={errors}
                />

                <FormInput
                  width={48}
                  label={'Plot Type'}
                  register={register}
                  register_key={'plotType'}
                  isRequired={true}
                  type={'dropdown'}
                  options={[
                    { value: 'regular', label: 'Regular' },
                    { value: 'irregular', label: 'Irregular' }
                  ]}
                  validation={{ required: 'Plot Type is required' }}
                  errors={errors}
                />
              </div>

              {/* Conditionally render fields based on Plot Type */}
              {watchedPlotType === 'regular' ? (
                <div className="flex justify-between items-center w-full">
                  <FormInput
                    key={'width'}
                    width={32}
                    label={`Width (${measurementType === 'sq/mt' ? 'mt' : 'ft'})`}
                    register={register}
                    register_key={'width'}
                    isRequired={true}
                    type={'text'}
                    onType={(val) => handleNumericInput('width', val)}
                    validation={{
                      required: 'Width is required',
                      pattern: {
                        value: /^[0-9]*\.?[0-9]+$/,
                        message: 'Width must be a number'
                      }
                    }}
                    errors={errors}
                  />
                  <FormInput
                    key={'length'}
                    width={32}
                    label={`Depth (${measurementType === 'sq/mt' ? 'mt' : 'ft'})`}
                    register={register}
                    register_key={'length'}
                    isRequired={true}
                    type={'text'}
                    onType={(val) => handleNumericInput('length', val)}
                    validation={{
                      required: 'Depth is required',
                      pattern: {
                        value: /^[0-9]*\.?[0-9]+$/,
                        message: 'Depth must be a number'
                      }
                    }}
                    errors={errors}
                  />

                  <FormInput
                    key={'measurementUnit'}
                    width={32}
                    label={'Measurement Unit'}
                    register={register}
                    register_key={'measurementUnit'}
                    isRequired={true}
                    type={'text'}
                    valuePre={measurementType ? measurementType : 'sq/ft'}
                    readOnly={true} // Read-only as it's predefined
                    validation={{ required: 'Measurement Unit is required' }}
                    errors={errors}
                  />
                </div>
              ) : (
                <div className="flex justify-between gap-2 flex-nowrap">
                  {/* East, West, North, South Fields */}
                  <FormInput
                    key={'east_length'}
                    width={18}
                    label={`East (${measurementType === 'sq/mt' ? 'mt' : 'ft'})`}
                    register={register}
                    register_key={'east_length'}
                    isRequired={true}
                    type={'text'}
                    validation={{
                      required: 'East is required'
                    }}
                    errors={errors}
                  />
                  <FormInput
                    key={'west_length'}
                    width={18}
                    label={`West (${measurementType === 'sq/mt' ? 'mt' : 'ft'})`}
                    register={register}
                    register_key={'west_length'}
                    isRequired={true}
                    type={'text'}
                    validation={{
                      required: 'West is required'
                    }}
                    errors={errors}
                  />
                  <FormInput
                    key={'north_length'}
                    width={18}
                    label={`North (${measurementType === 'sq/mt' ? 'mt' : 'ft'})`}
                    register={register}
                    register_key={'north_length'}
                    isRequired={true}
                    type={'text'}
                    validation={{
                      required: 'North is required'
                    }}
                    errors={errors}
                  />
                  <FormInput
                    key={'south_length'}
                    width={18}
                    label={`South (${measurementType === 'sq/mt' ? 'mt' : 'ft'})`}
                    register={register}
                    register_key={'south_length'}
                    isRequired={true}
                    type={'text'}
                    validation={{
                      required: 'South is required'
                    }}
                    errors={errors}
                  />

                  <FormInput
                    key={'measurementUnitirregular'}
                    width={16}
                    label={'Measurement Unit'}
                    register={register}
                    register_key={'measurementUnit'}
                    isRequired={true}
                    type={'text'}
                    valuePre={measurementType ? measurementType : 'sq/ft'}
                    readOnly={true} // Read-only as it's predefined
                    validation={{ required: 'Measurement Unit is required' }}
                    errors={errors}
                  />
                </div>
              )}

              {/* Build Up Area and Carpet Area */}
              <div className="flex justify-between items-center w-full">
                {watchedPlotType === 'regular' ? (
                  <FormInput
                    width={32}
                    label={`Plot Area (${measurementType ? measurementType : 'sq/ft'})`}
                    register={register}
                    register_key={'plotArea'}
                    isRequired={true}
                    type={'text'}
                    valuePre={plotArea}
                    readOnly={true}
                    validation={{
                      required: 'Plot Area is required',
                      pattern: {
                        value: /^[0-9]*\.?[0-9]+$/,
                        message: 'Plot Area must be a number'
                      }
                    }}
                    errors={errors}
                  />
                ) : (
                  <FormInput
                    width={32}
                    label={`Plot Area (${measurementType ? measurementType : 'sq/ft'})`}
                    register={register}
                    register_key={'plotArea'}
                    isRequired={true}
                    type={'text'}
                    onType={handlePlotAreaChange}
                    placeholder="Enter plot area manually"
                    validation={{
                      required: 'Plot Area is required for Irregular plots',
                      pattern: {
                        value: /^[0-9]*\.?[0-9]+$/,
                        message: 'Plot Area must be a number'
                      }
                    }}
                    errors={errors}
                  />
                )}
                <FormInput
                  width={32}
                  label={`Build Up Area (${measurementType ? measurementType : 'sq/ft'})`}
                  register={register}
                  register_key={'buildUpArea'}
                  isRequired={false}
                  type={'text'}
                  onType={handleBuildUpAreaChange}
                  validation={{
                    required: 'Build Up Area is required',
                    pattern: {
                      value: /^[0-9]*\.?[0-9]+$/,
                      message: 'Build Up Area must be a number'
                    }
                  }}
                  errors={errors}
                />

                <FormInput
                  width={32}
                  label={`Carpet Area (${measurementType ? measurementType : 'sq/ft'})`}
                  register={register}
                  register_key={'carpetArea'}
                  isRequired={false}
                  type={'text'}
                  onType={handleCarpetAreaChange}
                  validation={{
                    required: 'Carpet Area is required',
                    pattern: {
                      value: /^[0-9]*\.?[0-9]+$/,
                      message: 'Carpet Area must be a number'
                    }
                  }}
                  errors={errors}
                />
              </div>

              {/* Number of Floors - Only show if buildUpArea > 0 */}
              {isShow === 'INDEPENDENT' && parseFloat(watch('buildUpArea')) > 0 && (
                <div className="flex justify-between items-center w-full">
                  <FormInput
                    width={48}
                    label={'Accomodation'}
                    register={register}
                    register_key={'tag'}
                    isRequired={true}
                    type={'dropdown'}
                    options={[
                      { value: '', label: 'Select Accomodation', disabled: true, selected: true },
                      { value: '1BHK', label: '1BHK' },
                      { value: '1.5BHK', label: '1.5BHK' },
                      { value: '2BHK', label: '2BHK' },
                      { value: '2.5BHK', label: '2.5BHK' },
                      { value: '3BHK', label: '3BHK' },
                      { value: '3.5BHK', label: '3.5BHK' },
                      { value: '4BHK', label: '4BHK' },
                      { value: '4.5BHK', label: '4.5BHK' },
                      { value: '5BHK', label: '5BHK' },
                      { value: '5.5BHK', label: '5.5BHK' },
                      { value: 'Other', label: 'Other' }
                    ]}
                    validation={{ required: 'Accomodation is required' }}
                    errors={errors}
                  />

                  <FormInput
                    width={48}
                    label={'Number of Floors'}
                    register={register}
                    register_key={'numberOfFloors'}
                    isRequired={true}
                    type={'text'}
                    onType={(val) => {
                      const cleanedValue = val.replace(/[^0-9]/g, '');
                      setValue('numberOfFloors', cleanedValue);
                    }}
                    validation={{
                      required: 'Number of Floors is required',
                      pattern: {
                        value: /^[0-9]+$/,
                        message: 'Please enter a valid number'
                      },
                      min: {
                        value: 1,
                        message: 'Number of floors must be at least 1'
                      }
                    }}
                    errors={errors}
                  />
                </div>
              )}

              {/* File Uploads */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full items-start">
                <FormInput
                  width={100}
                  register={register}
                  register_key={'floorPlanLink'}
                  isRequired={false}
                  type={'file-btn'}
                  valuePre={'dummy-link'}
                  label={'Add Floor Plan'}
                  errors={errors}
                />
                <FormInput
                  width={100}
                  register={register}
                  register_key={'unitSpecification'}
                  isRequired={false}
                  type={'file-btn'}
                  valuePre={'dummy-link'}
                  label={'Unit Specification'}
                  errors={errors}
                />
                <div className="flex flex-col gap-2 w-full">
                  <span className="text-gray-600 text-sm">CAD File</span>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept=".dwg,.dxf,.pdf,.cad"
                      onChange={handleCadFileChange}
                      disabled={isUploadingCadFile}
                    />
                    <span
                      className={`flex justify-center items-center w-full px-4 py-2 rounded border border-stone-300 text-sm ${isUploadingCadFile ? 'opacity-50 cursor-not-allowed' : 'hover:bg-stone-50'}`}>
                      {isUploadingCadFile ? 'Uploading...' : 'Choose CAD File'}
                    </span>
                  </label>
                  {cadFileLink?.length > 0 && (
                    <div className="flex flex-col gap-1">
                      {cadFileLink.map((file, idx) => (
                        <div
                          key={`${file.url}-${idx}`}
                          className="flex items-center gap-2 text-xs text-gray-600 max-w-[220px]">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="truncate text-blue-600 hover:underline"
                            title={file.name}>
                            ✓ {file.name}
                          </a>
                          <button
                            type="button"
                            onClick={() => removeCadFile(idx)}
                            className="text-red-500 hover:text-red-700 shrink-0"
                            title="Remove">
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* <FormInput
                  width={32}
                  register={register}
                  register_key={'materialListLink'}
                  isRequired={false}
                  type={'file-btn'}
                  valuePre={'dummy-link'}
                  label={'Material List'}
                  errors={errors}
                /> */}
              </div>
              {/* Submit Buttons */}
              <div className="w-full flex justify-end items-center space-x-4 mt-8">
                <Button type="reset" onClick={onClose} displayType="secondary">
                  Cancel
                </Button>
                <Button type="submit">Add</Button>
              </div>
            </div>
          )}
          {/* GROUP Accommodation Type */}
          {isShow === 'GROUP' && (
            <div className="flex flex-col gap-6">
              <div className="flex justify-between items-center w-full">
                <FormInput
                  width={48}
                  label={`Build Up Area (${measurementType ? measurementType : 'sq/ft'})`}
                  register={register}
                  register_key={'buildUpArea'}
                  isRequired={true}
                  type={'text'}
                  onType={handleBuildUpAreaChange}
                  validation={{
                    required: 'Build Up Area is required',
                    pattern: {
                      value: /^[0-9]*\.?[0-9]+$/,
                      message: 'Build Up Area must be a number'
                    }
                  }}
                  errors={errors}
                />

                <FormInput
                  width={48}
                  label={`Carpet Area (${measurementType ? measurementType : 'sq/ft'})`}
                  register={register}
                  register_key={'carpetArea'}
                  isRequired={true}
                  type={'text'}
                  onType={handleCarpetAreaChange}
                  validation={{
                    required: 'Carpet Area is required',
                    pattern: {
                      value: /^[0-9]*\.?[0-9]+$/,
                      message: 'Carpet Area must be a number'
                    }
                  }}
                  errors={errors}
                />
              </div>
              <div className="flex justify-between items-center w-full">
                {isShow === 'GROUP' && parseFloat(watch('buildUpArea')) > 0 && (
                  <FormInput
                    width={48}
                    label={'Accomodation'}
                    register={register}
                    register_key={'tag'}
                    isRequired={true}
                    type={'dropdown'}
                    options={[
                      { value: '', label: 'Select Accomodation', disabled: true, selected: true },
                      { value: '1BHK', label: '1BHK' },
                      { value: '1.5BHK', label: '1.5BHK' },
                      { value: '2BHK', label: '2BHK' },
                      { value: '2.5BHK', label: '2.5BHK' },
                      { value: '3BHK', label: '3BHK' },
                      { value: '3.5BHK', label: '3.5BHK' },
                      { value: '4BHK', label: '4BHK' },
                      { value: '4.5BHK', label: '4.5BHK' },
                      { value: '5BHK', label: '5BHK' },
                      { value: '5.5BHK', label: '5.5BHK' },
                      { value: 'Other', label: 'Other' }
                    ]}
                    validation={{ required: 'Accomodation is required' }}
                    errors={errors}
                  />
                )}
                <FormInput
                  width={48}
                  label={'Unit Type'}
                  register={register}
                  register_key={'unitType'}
                  isRequired={true}
                  type={'dropdown'}
                  options={[
                    {
                      value: '',
                      label: 'Select type',
                      disabled: true
                    },
                    { value: 'Residential', label: 'Residential' },
                    { value: 'Commercial', label: 'Commercial' }
                  ]}
                  validation={{ required: 'Unit Type is required' }}
                  errors={errors}
                />
              </div>
              <div className="flex justify-between items-center w-full">
                <FormInput
                  width={48}
                  label="Loading Factor (%)"
                  register={register}
                  register_key="loadingFactor"
                  type="text"
                  onType={handleLoadingFactorChange}
                  validation={{
                    pattern: {
                      value: /^[0-9]*$/,
                      message: 'Loading Factor must be a number'
                    }
                  }}
                  errors={errors}
                />
                <FormInput
                  width={48}
                  register={register}
                  register_key="superBuildUpArea"
                  label={`Super Build Up Area (${measurementType ? measurementType : 'sq/ft'})`}
                  type="text"
                  onType={handleSuperBuildUpAreaChange}
                  validation={{
                    pattern: {
                      value: /^[0-9]*\.?[0-9]+$/,
                      message: 'Super Build Up Area must be a number'
                    }
                  }}
                  errors={errors}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full items-start">
                <FormInput
                  width={100}
                  register={register}
                  register_key={'floorPlanLink'}
                  isRequired={false}
                  type={'file-btn'}
                  label={'Add Floor Plan'}
                  errors={errors}
                />
                <FormInput
                  width={100}
                  register={register}
                  register_key={'unitSpecification'}
                  isRequired={false}
                  type={'file-btn'}
                  label={'Unit Specification'}
                  errors={errors}
                />
                <div className="flex flex-col gap-2 w-full">
                  <span className="text-gray-600 text-sm">CAD File</span>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept=".dwg,.dxf,.pdf,.cad"
                      onChange={handleCadFileChange}
                      disabled={isUploadingCadFile}
                    />
                    <span
                      className={`flex justify-center items-center w-full px-4 py-2 rounded border border-stone-300 text-sm ${isUploadingCadFile ? 'opacity-50 cursor-not-allowed' : 'hover:bg-stone-50'}`}>
                      {isUploadingCadFile ? 'Uploading...' : 'Choose CAD File'}
                    </span>
                  </label>
                  {cadFileLink?.length > 0 && (
                    <div className="flex flex-col gap-1">
                      {cadFileLink.map((file, idx) => (
                        <div
                          key={`${file.url}-${idx}`}
                          className="flex items-center gap-2 text-xs text-gray-600 max-w-[220px]">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="truncate text-blue-600 hover:underline"
                            title={file.name}>
                            ✓ {file.name}
                          </a>
                          <button
                            type="button"
                            onClick={() => removeCadFile(idx)}
                            className="text-red-500 hover:text-red-700 shrink-0"
                            title="Remove">
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* <div className="flex justify-between items-center w-full">
                <FormInput
                  width={46}
                  register={register}
                  register_key={'materialListLink'}
                  isRequired={false}
                  type={'file-btn'}
                  label={'Material List'}
                  errors={errors}
                />
              </div> */}

              {/* Submit Buttons */}
              <div className="w-full flex justify-end items-center space-x-4 mt-8">
                <Button type="reset" onClick={onClose} displayType="secondary">
                  Cancel
                </Button>
                <Button type="submit">Add</Button>
              </div>
            </div>
          )}

          {/* PARKING Accommodation Type */}
          {isShow === 'PARKING' && (
            <div className="flex flex-col gap-6">
              <div className="flex justify-between items-center w-full space-x-8">
                <FormInput
                  width={32}
                  label={'Unit Type'}
                  register={register}
                  register_key={'unitType'}
                  isRequired={true}
                  type={'dropdown'}
                  options={[
                    {
                      value: '',
                      label: 'Select type',
                      disabled: true
                    },
                    { value: '2-wheeler', label: '2-wheeler' },
                    { value: '4-wheeler', label: '4-wheeler' }
                  ]}
                  validation={{ required: 'Unit Type is required' }}
                  errors={errors}
                />

                <FormInput
                  width={32}
                  label={`Parking Space Type (${measurementType ? measurementType : 'sq/ft'})`}
                  register={register}
                  register_key={'buildUpArea'}
                  isRequired={true}
                  type={'text'}
                  onType={handleBuildUpAreaChange}
                  validation={{
                    required: 'Parking Space Type is required',
                    pattern: {
                      value: /^[0-9]*\.?[0-9]+$/,
                      message: 'Parking Space Type must be a number'
                    }
                  }}
                  errors={errors}
                />

                <input
                  type="hidden"
                  name="accomodationType"
                  value="PARKING"
                  {...register('accomodationType')}
                />

                <div className="text-md flex flex-col justify-start gap-2 w-[48%] pl-6">
                  <span className="text-gray-600">
                    Electrified <span className="text-red-500"> *</span>
                  </span>
                  <div className="w-fit flex justify-start space-x-6">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="electrified"
                        id="electrifiedYes"
                        value="Yes"
                        {...register('electrified', { required: 'Electrified is required' })}
                      />
                      <label htmlFor="electrifiedYes">Yes</label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="electrified"
                        id="electrifiedNo"
                        value="No"
                        {...register('electrified', { required: 'Electrified is required' })}
                      />
                      <label htmlFor="electrifiedNo">No</label>
                    </div>
                  </div>
                  {errors.electrified && (
                    <p className="text-red-500 text-xs mt-1">{errors.electrified.message}</p>
                  )}
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="w-full flex justify-end items-center space-x-4 mt-8">
                <Button type="reset" onClick={onClose} displayType="secondary">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Add
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
